// src/pages/UserDashboard.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Select,
  Heading,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import axiosInstance from "../api/axiosConfig";

// Componentes
import VehicleLocationsMapGrouped from "./VehicleLocationsMapGrouped";
// Nuevos componentes basados en Nivo
import VehicleLocationsBarChart from "./VehicleLocationsBarChart";
import VehicleLocationsPieChart from "./VehicleLocationsPieChart";
import VehicleLocationsLineChart from "./VehicleLocationsLineChart";

import VehicleLocationsTable from "./VehicleLocationsTable";
import SideLists from "./SideLists";

// Definición de colores corporativos (versión clara)
const colors = {
  background: "#f8fafc", // Fondo principal claro
  cardBg: "#ffffff", // Fondo de tarjetas blanco
  headerBg: "#f1f5f9", // Cabecera suave
  primary: "#2563eb", // Azul corporativo
  secondary: "#4f46e5", // Púrpura corporativo
  accent: "#059669", // Verde corporativo
  text: "#1e293b", // Texto principal oscuro
  textMuted: "#64748b", // Texto secundario
  border: "#e2e8f0", // Bordes suaves
  hover: "#f8fafc", // Color para hover
  shadow:
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)", // Sombras suaves
};

const groupOptions = [
  { value: "province", label: "Provincia" },
  { value: "canton", label: "Cantón" },
  { value: "parroquia", label: "Parroquia" },
];

const UserDashboard = () => {
  const [loading, setLoading] = useState(true);

  // Data del mapa (siempre parroquia)
  const [mapDayData, setMapDayData] = useState([]);
  const [mapNightData, setMapNightData] = useState([]);

  // Data segregada (día/noche) para provincia, cantón, parroquia
  const [dayDataProvince, setDayDataProvince] = useState([]);
  const [nightDataProvince, setNightDataProvince] = useState([]);
  const [dayDataCanton, setDayDataCanton] = useState([]);
  const [nightDataCanton, setNightDataCanton] = useState([]);
  const [dayDataParroquia, setDayDataParroquia] = useState([]);
  const [nightDataParroquia, setNightDataParroquia] = useState([]);

  // Nivel de agrupación para gráfica/tabla
  const [groupLevel, setGroupLevel] = useState("province");

  // Estado para el ítem seleccionado (para interactividad entre componentes)
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [resProvince, resCanton, resParroquia] = await Promise.all([
          axiosInstance.get(
            "/vehicle-locations-cantons/grouped-dynamic?by=province"
          ),
          axiosInstance.get(
            "/vehicle-locations-cantons/grouped-dynamic?by=canton"
          ),
          axiosInstance.get(
            "/vehicle-locations-cantons/grouped-dynamic?by=parroquia"
          ),
        ]);

        setDayDataProvince(resProvince.data.day || []);
        setNightDataProvince(resProvince.data.night || []);

        setDayDataCanton(resCanton.data.day || []);
        setNightDataCanton(resCanton.data.night || []);

        setDayDataParroquia(resParroquia.data.day || []);
        setNightDataParroquia(resParroquia.data.night || []);

        // Mapa siempre muestra data de parroquias
        setMapDayData(resParroquia.data.day || []);
        setMapNightData(resParroquia.data.night || []);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Seleccionar datos según groupLevel
  let dayDataSelected = [];
  let nightDataSelected = [];
  if (groupLevel === "province") {
    dayDataSelected = dayDataProvince;
    nightDataSelected = nightDataProvince;
  } else if (groupLevel === "canton") {
    dayDataSelected = dayDataCanton;
    nightDataSelected = nightDataCanton;
  } else {
    dayDataSelected = dayDataParroquia;
    nightDataSelected = nightDataParroquia;
  }

  if (loading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg={colors.background}>
        <Spinner size="xl" color={colors.primary} thickness="4px" />
      </Flex>
    );
  }

  // Callbacks para interactividad
  const handleChartItemSelect = (locationName) => {
    setSelectedLocation(locationName);
  };

  const handleTableRowSelect = (locationName) => {
    setSelectedLocation(locationName);
  };

  return (
    <Box
      p={5}
      bg={colors.background}
      minH="100vh"
      color={colors.text}
      fontFamily="'Inter', 'Segoe UI', sans-serif"
    >
      <Flex gap={5} flexWrap="wrap">
        <SideLists
          dayProvince={dayDataProvince}
          dayCanton={dayDataCanton}
          dayParroquia={dayDataParroquia}
          nightProvince={nightDataProvince}
          nightCanton={nightDataCanton}
          nightParroquia={nightDataParroquia}
          colors={colors}
        />

        <Box flex="1" minW="600px">
          {/* Filtro */}
          <Card
            mb={4}
            bg={colors.cardBg}
            borderRadius="12px"
            boxShadow={colors.shadow}
            border={`1px solid ${colors.border}`}
          >
            <CardHeader
              bg={colors.headerBg}
              color={colors.text}
              p={4}
              borderTopRadius="12px"
              borderBottom={`1px solid ${colors.border}`}
            >
              <Heading
                as="h4"
                size="md"
                color={colors.primary}
                fontWeight="600"
              >
                Agrupar por
              </Heading>
            </CardHeader>
            <CardBody p={4}>
              <Select
                width="250px"
                value={groupLevel}
                onChange={(e) => {
                  setGroupLevel(e.target.value);
                  setSelectedLocation(null);
                }}
                bg={colors.cardBg}
                color={colors.text}
                borderColor={colors.border}
                _hover={{
                  borderColor: colors.primary,
                }}
                _focus={{
                  borderColor: colors.primary,
                  boxShadow: `0 0 0 1px ${colors.primary}`,
                }}
              >
                {groupOptions.map((opt) => (
                  <option
                    key={opt.value}
                    value={opt.value}
                    style={{
                      background: colors.cardBg,
                      color: colors.text,
                    }}
                  >
                    {opt.label}
                  </option>
                ))}
              </Select>
            </CardBody>
          </Card>

          <Flex gap={4} flexWrap="wrap">
            {/* Mapa */}
            <Card
              flex="1"
              bg={colors.cardBg}
              borderRadius="12px"
              boxShadow={colors.shadow}
              border={`1px solid ${colors.border}`}
            >
              <CardHeader
                bg={colors.headerBg}
                color={colors.text}
                p={4}
                borderTopRadius="12px"
                borderBottom={`1px solid ${colors.border}`}
              >
                <Heading
                  as="h4"
                  size="md"
                  color={colors.primary}
                  fontWeight="600"
                >
                  Mapa - Parroquias
                </Heading>
              </CardHeader>
              <CardBody height="420px" p={0}>
                <VehicleLocationsMapGrouped
                  dayData={mapDayData}
                  nightData={mapNightData}
                  highlightLocation={selectedLocation}
                  groupLevel={groupLevel}
                  colors={colors}
                />
              </CardBody>
            </Card>

            {/* Gráficas con Tabs */}
            <Card
              flex="1"
              bg={colors.cardBg}
              borderRadius="12px"
              boxShadow={colors.shadow}
              border={`1px solid ${colors.border}`}
            >
              <CardHeader
                bg={colors.headerBg}
                color={colors.text}
                p={4}
                borderTopRadius="12px"
                borderBottom={`1px solid ${colors.border}`}
              >
                <Heading
                  as="h4"
                  size="md"
                  color={colors.primary}
                  fontWeight="600"
                >
                  {groupLevel.toUpperCase()} (Diurno vs. Nocturno)
                </Heading>
              </CardHeader>
              <CardBody>
                <Tabs isFitted colorScheme="blue" variant="enclosed">
                  <TabList mb="1em">
                    <Tab
                      _selected={{
                        color: colors.primary,
                        borderColor: colors.primary,
                        borderBottom: "none",
                        bg: colors.cardBg,
                      }}
                      color={colors.textMuted}
                      bg={colors.headerBg}
                      _hover={{
                        color: colors.primary,
                        bg: colors.hover,
                      }}
                    >
                      Pie
                    </Tab>
                    <Tab
                      _selected={{
                        color: colors.primary,
                        borderColor: colors.primary,
                        borderBottom: "none",
                        bg: colors.cardBg,
                      }}
                      color={colors.textMuted}
                      bg={colors.headerBg}
                      _hover={{
                        color: colors.primary,
                        bg: colors.hover,
                      }}
                    >
                      Bar
                    </Tab>
                    <Tab
                      _selected={{
                        color: colors.primary,
                        borderColor: colors.primary,
                        borderBottom: "none",
                        bg: colors.cardBg,
                      }}
                      color={colors.textMuted}
                      bg={colors.headerBg}
                      _hover={{
                        color: colors.primary,
                        bg: colors.hover,
                      }}
                    >
                      Line
                    </Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      <VehicleLocationsPieChart
                        dayData={dayDataSelected}
                        nightData={nightDataSelected}
                        groupLevel={groupLevel}
                        onItemSelect={handleChartItemSelect}
                        colors={colors}
                      />
                    </TabPanel>
                    <TabPanel>
                      <VehicleLocationsBarChart
                        dayData={dayDataSelected}
                        nightData={nightDataSelected}
                        groupLevel={groupLevel}
                        onItemSelect={handleChartItemSelect}
                        colors={colors}
                      />
                    </TabPanel>
                    <TabPanel>
                      <VehicleLocationsLineChart
                        dayData={dayDataSelected}
                        nightData={nightDataSelected}
                        groupLevel={groupLevel}
                        onItemSelect={handleChartItemSelect}
                        colors={colors}
                      />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </CardBody>
            </Card>
          </Flex>

          {/* Tabla */}
          <Card
            mt={5}
            bg={colors.cardBg}
            borderRadius="12px"
            boxShadow={colors.shadow}
            border={`1px solid ${colors.border}`}
          >
            <CardHeader
              bg={colors.headerBg}
              color={colors.text}
              p={4}
              borderTopRadius="12px"
              borderBottom={`1px solid ${colors.border}`}
            >
              <Heading
                as="h4"
                size="md"
                color={colors.primary}
                fontWeight="600"
              >
                Listado Detallado: {groupLevel.toUpperCase()}
              </Heading>
            </CardHeader>
            <CardBody>
              <VehicleLocationsTable
                dayData={dayDataSelected}
                nightData={nightDataSelected}
                groupLevel={groupLevel}
                selectedLocation={selectedLocation}
                onRowSelect={handleTableRowSelect}
                colors={colors}
              />
            </CardBody>
          </Card>
        </Box>
      </Flex>
    </Box>
  );
};

export default UserDashboard;
